import {
  DEFAULT_BORDER_PACKS,
  DEFAULT_BUTTON_PACKS,
  DEFAULT_CORNER_PACKS,
  DEFAULT_PRESET,
  DEFAULT_SPACING_PACKS,
  DEFAULT_TEXT_PACKS,
} from '@modules/editor-sidebar';
import type {
  ColorPack,
  CornerPack,
  Preset,
  SpacingPack,
  TextPack,
  BorderPack,
  ButtonPack,
  PresetOptionText,
  PresetOptionColor,
  PresetOptionBorder,
  PresetOptionCorner,
  PresetOptionSpacing,
  PresetOptionButton,
} from '@modules/global-style';
import cloneDeep from 'lodash.clonedeep';
import { COLOR_KEYS_MAP } from '@modules/global-style';
import { app } from '@/store';
import emitter from '@/emitter';

const getElection = (values: string[], numberOfTopElection = 1): string | string[] => {
  if (!values || !values.length) {
    return numberOfTopElection > 1 ? [] : '';
  }

  if (numberOfTopElection > 1 && numberOfTopElection >= values.length) {
    return values;
  }

  const counts: any = {};
  for (const value of values) {
    counts[value] = (counts[value] || 0) + 1;
  }

  const sortedValues = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  const maxValues = sortedValues.slice(0, numberOfTopElection);

  return numberOfTopElection === 1 ? maxValues[0] : maxValues;
};

const getFontFamily = function ($elm: any) {
  let defaultFontFamily = 'inherit';
  const $parentElm = $elm.parents();
  const lengthParentsElm = $parentElm.length;
  for (let i = 0; i < lengthParentsElm; i++) {
    const $this = jQuery($parentElm.eq(i));
    if ($this.css('font-family') != 'inherit') {
      defaultFontFamily = $this.css('font-family');
      break;
    }
  }
  return defaultFontFamily && defaultFontFamily.split(',')[0];
};

/**
 * Extract styles from a DOM
 * [high priority] text tag: h1, h2, h3, h4, h5, h6,
 *                          p, a, span, div
 *      font-family, font-size, font-weight, font-style, text-decoration-line, line-height, letter-spacing
 *
 * color: color, background-color
 * tag: body, [role=main], #main, #content
 *
 * border: border-style, border-width, border-color
 *
 * corner: border-radius
 *
 * spacing: margin, padding
 *
 * [high priority] button tag: button, input[type="button"]
 * @param d JQuery DOM
 * @returns all partial styles can be extracted from DOM
 */
const generate = (d: JQuery, domType: string): Preset => {
  const presetResult: Preset = cloneDeep(DEFAULT_PRESET);

  // initialize
  const $jq = window.jQuery as any;
  ($jq.fn as any).exists = function () {
    if (this.length > 0) {
      return this;
    } else {
      return false;
    }
  };

  // TODO: check type of "d" and remove Object parse here
  const $body = $jq(d).contents().find('body');

  if (domType === 'this-page') {
    // TODO(thanhdx): exclude GP Editor UI Element (e.g. .gryffeditor !#divNewBlock)
    $body.find('.gf-tool, .gf-outline, .gf-margin, block-placeholder, #divNewBlock, .gf_add-content').remove();
  }

  // define selector & css props to get value from
  // const baseSelector = $jq(d).find("[role=main]").exists() || $jq(d).find("#main").exists() || $jq(d).find("#content").exists() || $body;
  const baseSelector = $body;
  const tagSelectorMap = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    p: 'p',
    a: 'a',
    span: 'span',
    // div: "div",
    button:
      "button:not([class*=owl]), input[type='button'], input[type='submit'], input[type='reset'], .button, .btn, .gf_button",
  };

  const textCssProps = [
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'text-decoration-line',
    'line-height',
    'letter-spacing',
    'color',
  ];
  const buttonCssProps = ['color', 'background-color', 'border-style', 'border-width', 'border-color', 'border-radius'];
  const marginCssProps = ['margin-top', 'margin-left', 'margin-bottom', 'margin-right'];
  const paddingCssProps = ['padding-top', 'padding-left', 'padding-bottom', 'padding-right'];
  const headingTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
  const textTagsMap = {
    H1: {
      name: 'Heading 1',
      key: 'heading-1',
    },
    H2: {
      name: 'Heading 2',
      key: 'heading-2',
    },
    H3: {
      name: 'Heading 3',
      key: 'heading-3',
    },
    H4: {
      name: 'Heading 4',
      key: 'heading-4',
    },
    H5: {
      name: 'Heading 5',
      key: 'heading-5',
    },
    H6: {
      name: 'Heading 6',
      key: 'heading-6',
    },
    P0: {
      name: 'Paragraph 1',
      key: 'paragraph-1',
    },
    P1: {
      name: 'Paragraph 2',
      key: 'paragraph-2',
    },
    P2: {
      name: 'Paragraph 3',
      key: 'paragraph-3',
    },
    P3: {
      name: 'Paragraph 4',
      key: 'paragraph-4',
    },
  };
  const borderMap = {
    solid: {
      name: 'Border Solid',
      key: 'border-solid',
    },
    dashed: {
      name: 'Border Dashed',
      key: 'border-dashed',
    },
    dotted: {
      name: 'Border Dotted',
      key: 'border-dotted',
    },
  };
  const spacingMap = [
    {
      name: 'XS',
      key: 'xs',
    },
    {
      name: 'S',
      key: 'sm',
    },
    {
      name: 'M',
      key: 'md',
    },
    {
      name: 'L',
      key: 'lg',
    },
    {
      name: 'XL',
      key: 'xl',
    },
  ];
  const buttonMap = [
    {
      name: 'Button',
      key: 'element',
    },
    {
      name: '(P) Cart Button',
      key: 'cart-button',
    },
    {
      name: '(P) View More',
      key: 'view-more',
    },
    {
      name: 'Submit Button',
      key: 'submit',
    },
    {
      name: '(P) Dynamic Checkout',
      key: 'check-button',
    },
  ];
  const cssProps = [...textCssProps, ...buttonCssProps, ...marginCssProps, ...paddingCssProps];
  const headings = {
    'font-family': [],
    'font-size': {
      H1: [],
      H2: [],
      H3: [],
      H4: [],
      H5: [],
      H6: [],
    },
    'font-weight': [],
    'font-style': [],
    'text-decoration-line': [],
    'line-height': [],
    'letter-spacing': [],
    color: [],
  };
  const paragraphs = {
    'font-family': [],
    'font-size': [],
    'font-weight': [],
    'font-style': [],
    'text-decoration-line': [],
    'line-height': [],
    'letter-spacing': [],
    color: [],
  };
  const buttons = {
    color: [],
    'background-color': [],
    'border-style': [],
    'border-width': [],
    'border-color': [],
    'border-radius': [],
  };
  const margins: string[] = [];
  const paddings: string[] = [];

  for (const tag in tagSelectorMap) {
    const $tags = baseSelector.find(Object(tagSelectorMap)[tag]).exists() || $jq(d).find(Object(tagSelectorMap)[tag]);
    if (!$tags) {
      continue;
    }

    $tags.each((_: any, tagEl: Element) => {
      const $tag = $(tagEl);
      if (!$tag) {
        return;
      }

      const propValues = $tag.css(cssProps);
      for (const prop of cssProps) {
        if (
          !propValues[prop] ||
          propValues[prop] === 'initial' ||
          propValues[prop] === 'inherit' ||
          propValues[prop] === 'transparent' ||
          propValues[prop] === 'none' ||
          propValues[prop] === 'rgba(0, 0, 0, 0)' ||
          propValues[prop] === 'rgb(255, 255, 255)' ||
          propValues[prop] === 'white'
        ) {
          continue;
        }

        // TODO (thanhdx): only push if match element type: headings, button, paragraphs...
        if (textCssProps.includes(prop) && $tag.is('h1, h2, h3, h4, h5, h6, p, span')) {
          const tagName = $tag.prop('tagName') || '';
          if (headingTags.includes(tagName)) {
            if (prop === 'font-size') {
              Object(headings)['font-size'][tagName].push(propValues[prop]);
            } else if (prop === 'font-family') {
              if (propValues[prop].split(',')[0] == 'inherit') {
                const parentCss = getFontFamily($tag);
                Object(headings)[prop].push(parentCss);
              } else {
                Object(headings)[prop].push(propValues[prop].split(',')[0]);
              }
            } else {
              Object(headings)[prop].push(propValues[prop]);
            }
          } else {
            if (prop === 'font-family') {
              if (propValues[prop].split(',')[0] == 'inherit') {
                const parentCss = getFontFamily($tag);
                Object(paragraphs)[prop].push(parentCss);
              } else {
                Object(paragraphs)[prop].push(propValues[prop].split(',')[0]);
              }
            } else {
              Object(paragraphs)[prop].push(propValues[prop]);
            }
          }
        }

        if (buttonCssProps.includes(prop) && $tag.is(tagSelectorMap.button)) {
          Object(buttons)[prop].push(propValues[prop]);
        }

        if (marginCssProps.includes(prop)) {
          margins.push(propValues[prop]);
        }

        if (paddingCssProps.includes(prop)) {
          paddings.push(propValues[prop]);
        }
      }
    });
  }
  const bodyProps = $body.css(['font-size', 'background-color', 'color']);

  // console.log("crawl: ", {headings, paragraphs, buttons, margins, paddings, bodyProps});

  // accounting & election
  // console.log({headings, paragraphs, buttons, margins, paddings});
  const headingElection = {
    'font-family': getElection(headings['font-family']),
    'font-size': {
      H1: getElection(headings['font-size']['H1']),
      H2: getElection(headings['font-size']['H2']),
      H3: getElection(headings['font-size']['H3']),
      H4: getElection(headings['font-size']['H4']),
      H5: getElection(headings['font-size']['H5']),
      H6: getElection(headings['font-size']['H6']),
    },
    'font-weight': getElection(headings['font-weight']),
    'font-style': getElection(headings['font-style']) || 'normal',
    'text-decoration-line': getElection(headings['text-decoration-line']),
    'line-height': getElection(headings['line-height']),
    'letter-spacing': getElection(headings['letter-spacing']),
    color: Object(window).tinycolor(getElection(headings['color'])).toHexString(),
  };

  const paragraphElection = {
    'font-family': getElection(paragraphs['font-family']),
    'font-size': getElection(paragraphs['font-size'], 4),
    'font-weight': getElection(paragraphs['font-weight']),
    'font-style': getElection(paragraphs['font-style']),
    'text-decoration-line': getElection(paragraphs['text-decoration-line']),
    'line-height': getElection(paragraphs['line-height']),
    'letter-spacing': getElection(paragraphs['letter-spacing']),
    color: Object(window).tinycolor(getElection(paragraphs['color'])).toHexString(),
  };

  const buttonElection = {
    color: Object(window).tinycolor(getElection(buttons['color'])).toHexString(),
    'background-color': Object(window).tinycolor(getElection(buttons['background-color'])).toHexString(),
    'border-style': getElection(buttons['border-style']),
    'border-width': getElection(buttons['border-width']),
    'border-color': Object(window).tinycolor(getElection(buttons['border-color'])).toHexString(),
    'border-radius': getElection(buttons['border-radius']),
  };

  // console.log("election: ", {headingElection, paragraphElection, buttonElection: getElection(buttons["background-color"])});

  // TODO(thanhdx): optimize use margins & paddings for spacings
  // const marginElection = getElection(margins);
  // const paddingElection = getElection(paddings);

  const DEFAULT_FONT_SIZE = 16;
  const baseSize = parseInt(bodyProps ? bodyProps['font-size'] : DEFAULT_FONT_SIZE) + 'px';
  // TODO(thanhdx): calculate scale based on all headings font size
  const scale = '1.333';
  const brandColor = buttonElection['background-color'] as string;
  const neutral1 = bodyProps ? Object(window).tinycolor(bodyProps['background-color']).toHexString() : '';
  const neutral3 = bodyProps ? Object(window).tinycolor(bodyProps['color']).toHexString() : '';
  const neutral2 = Object(window).tinycolor.mix(neutral1, neutral3, 50).toHexString();

  const accent2 = Object(window).tinycolor(brandColor).isDark()
    ? Object(window).tinycolor(brandColor).complement().lighten(20).toHexString()
    : Object(window).tinycolor(brandColor).complement().toHexString();
  const accent1 = Object(window).tinycolor(accent2).analogous(6, 30)[1].lighten(10).toHexString();
  const accent3 = Object(window).tinycolor(accent2).analogous(6, 30)[5].darken(30).toHexString();
  // const excludeColors = [accent1, accent2, accent3, brandColor, neutral1, neutral2, neutral3];
  // const accentColors = (getElection([...headings["color"], ...paragraphs["color"], ...buttons["color"]], 6) as string[]).map(Object(window).tinycolor).map(a => Object(a).toHexString()).filter((color: string) => !excludeColors.includes(color));
  const colorPacks: ColorPack[] = [
    {
      brand: {
        color1: Object(window).tinycolor.mix(brandColor, 'white', 30).toHexString(),
        color2: Object(window).tinycolor(brandColor).toHexString(),
        color3: Object(window).tinycolor.mix(brandColor, 'black', 25).toHexString(),
      },
      neutral: {
        color1: neutral1,
        color2: neutral2,
        color3: neutral3,
      },
      accent: {
        color1: Object(window).tinycolor(accent1).toHexString(),
        color2: Object(window).tinycolor(accent2).toHexString(),
        color3: Object(window).tinycolor(accent3).toHexString(),
        color4: '',
        color5: '',
        color6: '',
        color7: '#00000000',
      },
    },
  ];

  const headingFontName =
    (headingElection['font-family'] as string).trim().replace(/"/g, '').replace('+', ' ') || 'inherit';
  const paragraphFontName =
    (paragraphElection['font-family'] as string).trim().replace(/"/g, '').replace('+', ' ') || 'inherit';
  // Add fonts to Font Manager
  const headingFont = {
    googleName: headingFontName,
    isActive: true,
    styleName:
      (headingElection['font-family'] as string).trim().replace(/"/g, '').replace('+', ' ').trim() || 'Inherit',
  };
  const paragraphFont = {
    googleName: paragraphFontName,
    isActive: true,
    styleName: (paragraphElection['font-family'] as string).trim().replace(/"/g, '').replace('+', ' ') || 'Inherit',
  };

  emitter.off('googleFontsLoaded');
  emitter.on('googleFontsLoaded', () => {
    const addFonts = [headingFont, paragraphFont];
    (app as any).$store.dispatch('font/addFontOptionsAndLoadGoogleFonts', {
      fonts: addFonts,
      needSave: true,
    });
  });

  // TODO(thanhdx): check error & remove type casting here
  // const textPacks: (TextPack & {tagName: string})[] = headingTags.map((hTag: string): TextPack & {tagName: string} => ({
  //   baseSize,
  //   scale,
  //   headingFont: headingFontName,
  //   paragraphFont: paragraphFontName,
  //   fontWeight: parseInt(Object(headingElection)["font-weight"]) as FontWeightValue,
  //   fontStyle: headingElection["font-style"] as FontStyleValue,
  //   decoration: headingElection["text-decoration-line"] as DecorationValue,
  //   fontSize: Object(headingElection)["font-size"][hTag] as string,
  //   lineHeight: headingElection["line-height"] as string,
  //   letterSpacing: headingElection["letter-spacing"] as string,
  //   fontColor: `var(--${COLOR_KEYS_MAP["neutral-color3"]})`,
  //   tagName: hTag,
  // })).concat([0,1,2,3].map((i: number): TextPack & {tagName: string} => ({
  //   baseSize,
  //   scale,
  //   headingFont: headingFontName,
  //   paragraphFont: paragraphFontName,
  //   fontWeight: parseInt(Object(paragraphElection)["font-weight"]) as FontWeightValue,
  //   fontStyle: paragraphElection["font-style"] as FontStyleValue,
  //   decoration: paragraphElection["text-decoration-line"] as DecorationValue,
  //   fontSize: Object(paragraphElection)["font-size"][i] as string,
  //   lineHeight: paragraphElection["line-height"] as string,
  //   letterSpacing: paragraphElection["letter-spacing"] as string,
  //   fontColor: `var(--${COLOR_KEYS_MAP["neutral-color3"]})`,
  //   tagName: `P${i}`,
  // })));

  const textPacks: (TextPack & { tagName: string })[] = DEFAULT_TEXT_PACKS.map(
    (textPack: TextPack, index): TextPack & { tagName: string } => ({
      ...textPack,
      baseSize,
      scale,
      headingFont: headingFontName,
      paragraphFont: paragraphFontName,
      fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
      tagName: index < 6 ? `H${index + 1}` : `P${index - 6}`,
    }),
  );

  const borderPacks: BorderPack[] = DEFAULT_BORDER_PACKS.map(
    (borderPack: BorderPack): BorderPack => ({
      ...borderPack,
      color: `var(--${COLOR_KEYS_MAP['neutral-color2']})`,
    }),
  );
  const cornerPacks: CornerPack[] = DEFAULT_CORNER_PACKS.map(
    (cornerPack: CornerPack): CornerPack => ({
      ...cornerPack,
      value: `${buttonElection['border-radius']} ${buttonElection['border-radius']} ${buttonElection['border-radius']} ${buttonElection['border-radius']}`,
    }),
  );
  const spacingPacks: SpacingPack[] = DEFAULT_SPACING_PACKS;
  // TODO(thanhdx): detail for buttons
  const buttonPacks: ButtonPack[] = DEFAULT_BUTTON_PACKS.map(
    (buttonPack: ButtonPack): ButtonPack => ({
      ...buttonPack,
      textColor: {
        origin: `var(--${COLOR_KEYS_MAP['neutral-color1']})`,
        hover: `var(--${COLOR_KEYS_MAP['neutral-color1']})`,
      },
      backgroundColor: {
        origin: `var(--${COLOR_KEYS_MAP['brand-color2']})`,
        hover: `var(--${COLOR_KEYS_MAP['brand-color1']})`,
      },
    }),
  );

  // convert to Preset
  presetResult.name = 'New Preset';
  presetResult.presetOptions = {
    texts: textPacks.map(
      (textPack: TextPack & { tagName: string }): PresetOptionText => ({
        name: Object(textTagsMap)[textPack.tagName].name,
        isDefault: true,
        type: 'text',
        key: Object(textTagsMap)[textPack.tagName].key,
        subType: textPack.tagName.includes('H') ? 'heading' : 'paragraph',
        value: textPack,
      }),
    ),
    colors: colorPacks.map(
      (colorPack: ColorPack): PresetOptionColor => ({
        name: 'Colors',
        isDefault: true,
        type: 'color',
        key: 'color',
        value: colorPack,
      }),
    ),
    borders: borderPacks.map(
      (borderPack: BorderPack): PresetOptionBorder => ({
        name: Object(borderMap)[borderPack.style].name,
        isDefault: true,
        type: 'border',
        key: Object(borderMap)[borderPack.style].key,
        value: borderPack,
      }),
    ),
    corners: cornerPacks.map(
      (cornerPack: CornerPack): PresetOptionCorner => ({
        name: 'Default Corner',
        isDefault: true,
        type: 'corner',
        key: 'corner',
        value: cornerPack,
      }),
    ),
    spacings: spacingPacks.map(
      (spacingPack: SpacingPack, index: any): PresetOptionSpacing => ({
        name: spacingMap[index].name,
        isDefault: true,
        type: 'spacing',
        key: spacingMap[index].key,
        value: spacingPack,
      }),
    ),
    buttons: buttonPacks.map(
      (buttonPack: ButtonPack, index: any): PresetOptionButton => ({
        name: buttonMap[index].name,
        isDefault: true,
        type: 'button',
        key: buttonMap[index].key,
        value: buttonPack,
      }),
    ),
  };

  return presetResult;
};

/**
 * Export
 */
export default (params: { dom: string | any }) => {
  return new Promise((resolve, reject) => {
    if (!params?.dom?.length) {
      reject();
    }

    let $dom: any;
    if (typeof params.dom === 'string') {
      $dom = $("<iframe style='display: none'></iframe>");
      Object($dom).attr('srcdoc', params.dom);
      $('#gfLeftSideBar').prepend($dom);
      $dom.on('load', () => {
        const preset = generate($dom, 'this-page');
        $dom.remove();
        resolve(preset);
      });
    } else {
      $dom = $(params.dom as any);
      resolve(generate($dom as any, 'live-theme'));
    }
  });
};
